import { REPORT_CONSTANTS, TRANSLATED_LABELS } from "~/helpers/constants";

/**
 * Traduz um tipo de relatório usando o mapa de labels (ex: "relacoes" → "Relações").
 */
function translateType(type: string): string {
  const normalized = type.toLowerCase();
  for (const key in TRANSLATED_LABELS) {
    if (key.toLowerCase() === normalized) {
      return TRANSLATED_LABELS[key as keyof typeof TRANSLATED_LABELS];
    }
  }
  return type;
}

/**
 * Traduz o prefixo de tipo no nome do relatório (ex: "relacoes ..." → "Relações ...").
 */
export function formatReportName(name: string): string {
  if (!name) return name;
  const firstSpace = name.indexOf(" ");
  const prefix = firstSpace > 0 ? name.slice(0, firstSpace) : name;
  const translated = translateType(prefix);
  if (translated !== prefix) {
    return firstSpace > 0 ? `${translated}${name.slice(firstSpace)}` : translated;
  }
  return name;
}

/**
 * Separa o contador (#N) do nome do relatório, se presente.
 * O backend usa o separador "#" antes do número sequencial.
 * Retorna { label, counter } onde counter é null se não identificado.
 */
export function parseReportCounter(name: string): { label: string; counter: string | null } {
  if (!name) return { label: name, counter: null };
  const match = name.match(/^(.+?)\s+#(\d+)$/);
  if (match) return { label: match[1], counter: match[2] };
  return { label: name, counter: null };
}

/**
 * Monta o nome de exibição do relatório a partir do tipo e search args.
 * Usado como fallback quando report_name ainda não foi definido (pending/error).
 * Espelha a lógica do backend (build_report_name) sem o contador.
 */
export function buildReportDisplayName(
  reportType: string,
  searchArgs: Record<string, unknown>,
): string {
  const translatedType = translateType(reportType);

  if (!searchArgs || typeof searchArgs !== "object") return translatedType;

  if (reportType === REPORT_CONSTANTS.types.relacoes) {
    const parts: string[] = [];
    for (const key of Object.keys(searchArgs).sort()) {
      if (key.startsWith("ignore_")) continue;
      const value = searchArgs[key];
      if (typeof value === "string" && value) {
        const docType = key.includes("_") ? key.replace(/_\d+$/, "") : key;
        parts.push(`${docType} ${value}`);
      }
    }
    if (parts.length > 0) return `${translatedType} ${parts.join(" / ")}`;
  } else if (reportType === REPORT_CONSTANTS.types.combinado) {
    const parts: string[] = [];
    const skipKeys = new Set(["ignore_consortiums", "ignore_other_contacts", "related_documents", "combinado"]);
    for (const [key, value] of Object.entries(searchArgs)) {
      if (skipKeys.has(key)) continue;
      const vals = Array.isArray(value) ? value : [value];
      for (const v of vals) {
        if (typeof v === "string" && v) parts.push(`${key} ${v}`);
      }
    }
    if (parts.length > 0) return `${translatedType} ${parts.join(" / ")}`;
  } else {
    const value = searchArgs[reportType];
    if (typeof value === "string" && value) return `${translatedType} ${value}`;
  }

  return translatedType;
}
